import { FileText, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { DdqSession } from '@/types/api.types';
import { SessionCard } from './SessionCard';
import { SessionStatusBadge } from './SessionStatusBadge';
import { AutomationRateBar } from './AutomationRateBar';
import { formatSessionDate, formatSessionDateFull } from '../utils/formatSessionDate';
import type { ExportFormat } from './SessionActions';

export interface SessionListProps {
  sessions: DdqSession[];
  viewMode: 'grid' | 'list';
  onSelectSession: (sessionId: string) => void;
  onReviewSession: (sessionId: string) => void;
  onExportSession: (sessionId: string, format: ExportFormat) => void;
  onDeleteSession: (sessionId: string) => void;
  className?: string;
}

export function SessionList({
  sessions,
  viewMode,
  onSelectSession,
  onReviewSession,
  onExportSession,
  onDeleteSession,
  className,
}: SessionListProps) {
  if (viewMode === 'grid') {
    return (
      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
          className
        )}
      >
        {sessions.map((session) => (
          <SessionCard
            key={session.id}
            session={session}
            onSelect={() => onSelectSession(session.id)}
            onReview={() => onReviewSession(session.id)}
            onExport={(format) => onExportSession(session.id, format)}
            onDelete={() => onDeleteSession(session.id)}
          />
        ))}
      </div>
    );
  }

  // List view - compact table-like layout
  return (
    <div className={cn('space-y-2', className)}>
      {/* Header */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
        <div className="col-span-4">Name</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-3">Automation</div>
        <div className="col-span-2">Created</div>
        <div className="col-span-1"></div>
      </div>

      {/* Rows */}
      {sessions.map((session) => (
        <div
          key={session.id}
          className="grid grid-cols-12 gap-4 px-4 py-3 items-center bg-card border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => onSelectSession(session.id)}
        >
          {/* Name */}
          <div className="col-span-12 md:col-span-4 flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" title={session.fileName}>
                {session.fileName}
              </p>
              <p className="text-xs text-muted-foreground">
                {session.totalQuestions} questions · {formatSessionDate(session.created)}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="col-span-6 md:col-span-2">
            <SessionStatusBadge status={session.status} />
          </div>

          {/* Automation */}
          <div className="col-span-6 md:col-span-3">
            <div className="flex items-center gap-2">
              <AutomationRateBar
                rate={session.automationRate}
                exactCount={session.exactMatches}
                suggestedCount={session.suggestedMatches}
                generatedCount={session.generatedResponses}
                noMatchCount={session.noMatches}
                className="flex-1 max-w-24"
              />
              <span className="text-sm font-medium w-12 text-right">
                {session.automationRate.toFixed(0)}%
              </span>
            </div>
          </div>

          {/* Created */}
          <div className="hidden md:block col-span-2 text-sm text-muted-foreground" title={formatSessionDateFull(session.created)}>
            {formatSessionDate(session.created)}
          </div>

          {/* Action */}
          <div
            className="col-span-12 md:col-span-1 flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              title="Open review"
              onClick={() => onReviewSession(session.id)}
              disabled={session.status !== 'Ready'}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
